<template>
    <div class="invoice-header-wrapper">
        <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 action-bar flex-wrap gap-3">
            <kv-button variant="text" prepend-icon="ui-chevron-left" @click="$emit('back')">
                {{ t('invoices-back_to_invoices') }}
            </kv-button>

            <div class="d-flex align-items-center actions-group flex-wrap gap-2">
                <div class="dropdown" v-if="!editMode">
                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" style="border-radius: 12px; font-size: 14px; font-weight: 500; padding: 8px 16px; display: inline-flex; align-items: center; border-color: #E5E7EB;">
                        {{ t('invoices-export') }}
                    </button>
                    <div class="dropdown-menu dropdown-menu-right shadow-sm border-0" style="border-radius: 8px;">
                        <a class="dropdown-item d-flex align-items-center py-2" href="#" @click.prevent="exportToPdf">
                            PDF
                        </a>
                        <a class="dropdown-item d-flex align-items-center py-2" href="#" @click.prevent="$emit('export-csv')">
                            CSV
                        </a>
                    </div>
                </div>

                <kv-button 
                    v-if="!isAdmin && canPay"
                    variant="filled" 
                    :prepend-icon="payIcon" 
                    @click="handlePay"
                >
                    {{ t('invoices-pay') }}
                </kv-button>

                <template v-if="isAdmin">
                    <kv-button v-if="!editMode" variant="outlined" prepend-icon="ui-edit-05" @click="$emit('start-edit')">
                        {{ t('invoices-edit_invoice') }}
                    </kv-button>
                    <template v-if="editMode">
                        <kv-button variant="filled" :prepend-icon="editSaving ? 'ui-loading-01' : 'ui-check'" @click="$emit('save-edit')" :disabled="editSaving">
                            {{ t('invoices-save_changes') }}
                        </kv-button>
                        <kv-button variant="outlined" prepend-icon="ui-x-close" @click="$emit('cancel-edit')" :disabled="editSaving">
                            {{ t('invoices-cancel') }}
                        </kv-button>
                    </template>
                </template>
            </div>
        </div>

        <div class="card header-card border-0 shadow-sm">
            <div class="card-body px-3 px-md-4 py-4">
                <div class="d-flex flex-column flex-lg-row align-items-start justify-content-between w-100" style="gap: 20px;">
                    
                    <div class="d-flex align-items-start header-left group-gap">
                        <div class="place-icon-container d-flex align-items-center justify-content-center">
                            <img
                                v-if="invoice.place_logo_url && !logoFailed"
                                :src="invoice.place_logo_url"
                                :alt="invoice.place_name"
                                @error="logoFailed = true"
                                class="place-logo"
                            />
                            <Icon v-else icon-class="ui-building-01" size="15" color="#2aaa4c" />
                        </div>
                        <div class="d-flex flex-column info-text">
                            <template v-if="editMode">
                                <input
                                    type="text"
                                    class="edit-field-input place-name-input mb-1"
                                    v-model="localPlaceLegalName"
                                    @input="emitHeaderChange"
                                    :placeholder="t('invoices-establishment_name')"
                                />
                            </template>
                            <h2 v-else class="place-name mb-1">{{ invoice.place_name || t('invoices-not_informed') }}</h2>
                            
                            <div class="establishment-meta mt-1">
                                <div class="meta-grid">
                                    <div class="meta-item">
                                        <Icon icon-class="ui-receipt" size="2" color="#9CA3AF" />
                                        <template v-if="editMode">
                                            <input
                                                type="text"
                                                class="edit-field-input small-input"
                                                v-model="localPlaceTaxId"
                                                @input="emitHeaderChange"
                                                :placeholder="t('invoices-cnpj')"
                                            />
                                        </template>
                                        <span v-else>{{ t('invoices-cnpj') }}: {{ invoice.place_tax_id || t('invoices-not_informed') }}</span>
                                    </div>
                                    <div class="meta-item">
                                        <Icon icon-class="ui-phone-call-01" size="2" color="#9CA3AF" />
                                        <template v-if="editMode">
                                            <input
                                                type="text"
                                                class="edit-field-input small-input mr-2 edit-field-phone"
                                                v-model="localPlacePhone"
                                                @input="emitHeaderChange"
                                                :placeholder="t('invoices-phone')"
                                            />
                                        </template>
                                        <span v-else class="mr-3">{{ invoice.place_phone || t('invoices-not_informed') }}</span>

                                        <Icon icon-class="ui-mail-01" size="2" color="#9CA3AF" />
                                        <template v-if="editMode">
                                            <input
                                                type="text"
                                                class="edit-field-input small-input edit-field-email"
                                                v-model="localPlaceEmail"
                                                @input="emitHeaderChange"
                                                :placeholder="t('invoices-email')"
                                            />
                                        </template>
                                        <span v-else>{{ invoice.place_email || t('invoices-not_informed') }}</span>
                                    </div>
                                    <div class="meta-item address-item">
                                        <Icon icon-class="ui-marker-pin-02" size="2" color="#9CA3AF" />
                                        <template v-if="editMode">
                                            <div class="d-flex flex-wrap edit-fields-gap">
                                                <input class="edit-field-input tiny-input edit-field-zip" v-model="localPlaceZipCode" @input="onZipCodeInput" @blur="fetchAddress" :placeholder="t('invoices-zip_code_short')" v-mask="'#####-###'" />
                                                <input class="edit-field-input tiny-input edit-field-address" v-model="localPlaceAddress1" @input="emitHeaderChange" :placeholder="t('invoices-address')" />
                                                <input class="edit-field-input tiny-input edit-field-complement" v-model="localPlaceAddress2" @input="emitHeaderChange" :placeholder="t('invoices-complement_short')" />
                                                <input class="edit-field-input tiny-input edit-field-zone" v-model="localPlaceZone" @input="emitHeaderChange" :placeholder="t('invoices-neighborhood')" />
                                                <input class="edit-field-input tiny-input edit-field-zone" v-model="localPlaceCity" @input="emitHeaderChange" :placeholder="t('invoices-city')" />
                                                <input class="edit-field-input tiny-input edit-field-state" v-model="localPlaceState" @input="emitHeaderChange" :placeholder="t('invoices-state_short')" />
                                            </div>
                                        </template>
                                        <span v-else>{{ fullAddress }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex flex-column align-items-start align-items-lg-end header-right mt-3 mt-lg-0 group-gap">
                        <div class="d-flex align-items-center status-group">
                            <span class="text-muted small mr-2">{{ t('invoices-status_label') }}:</span>
                            <invoice-status-changer
                                :invoice="invoice"
                                :is-admin="isAdmin"
                                @status-changed="$emit('status-changed', $event)"
                                @scroll-to-bank-slip="scrollToBankSlip"
                                @error="statusError = $event"
                            />
                        </div>

                        <div class="invoice-details-column d-flex flex-column align-items-start align-items-lg-end w-100 mt-1">
                            <template v-if="editMode">
                                <input
                                    type="text"
                                    class="edit-field-input title-input-right mb-2 text-md-right"
                                    :value="localTitle"
                                    @input="localTitle = $event.target.value; emitHeaderChange()"
                                    :placeholder="t('invoices-invoice_title')"
                                />
                            </template>
                            <h4 v-else class="invoice-title-right mb-1">{{ invoice.title || t('invoices-not_informed') }}</h4>

                            <div class="period-badge mb-2">
                                <span class="text-muted">{{ t('invoices-period_label') }}</span>
                                <template v-if="editMode">
                                    <div class="d-flex align-items-center ml-2 edit-fields-gap">
                                        <kv-date-picker v-model="localBillingCycleStart" @input="emitHeaderChange" dense class="edit-field-date" value-format="yyyy-MM-dd" />
                                        <span>-</span>
                                        <kv-date-picker v-model="localBillingCycleEnd" @input="emitHeaderChange" dense class="edit-field-date" value-format="yyyy-MM-dd" />
                                    </div>
                                </template>
                                <span v-else class="val">{{ formattedPeriodLabel || t('invoices-not_informed') }}</span>
                            </div>

                            <div class="d-flex align-items-center dates-group flex-wrap justify-content-lg-end mb-1" style="gap: 8px 24px;">
                                <div class="d-flex align-items-center mb-1">
                                    <Icon icon-class="ui-calendar" size="3" color="#9CA3AF" class="mr-2" />
                                    <span class="text-muted small mr-1">{{ t('invoices-issue_date_label') }}</span>
                                    <template v-if="editMode">
                                        <kv-date-picker 
                                            class="date-input-edit ml-1"
                                            v-model="localCreatedAt" 
                                            @input="emitHeaderChange"
                                            dense
                                            value-format="yyyy-MM-dd"
                                        />
                                    </template>
                                    <span v-else class="date-val">{{ invoice.created_at ? formatDate(invoice.created_at) : t('invoices-not_informed') }}</span>
                                </div>
                                <div class="d-flex align-items-center mb-1">
                                    <Icon icon-class="ui-calendar-date" size="3" color="#9CA3AF" class="mr-2" />
                                    <span class="text-muted small mr-1">{{ t('invoices-due_date_label') }}</span>
                                    
                                    <template v-if="editMode">
                                        <kv-date-picker 
                                            class="date-input-edit ml-1"
                                            v-model="localDueDate" 
                                            @input="emitHeaderChange"
                                            dense
                                            value-format="yyyy-MM-dd"
                                        />
                                    </template>
                                    <span v-else class="date-val">{{ invoice.due_date ? formatDate(invoice.due_date) : t('invoices-not_informed') }}</span>
                                </div>
                            </div>

                            <transition name="fade">
                                <div v-if="statusError" class="header-error-area d-flex flex-column align-items-end mt-1">
                                    <div class="text-danger small font-weight-500 mb-1 text-right">
                                        <i class="fa fa-info-circle mr-1"></i>
                                        {{ statusError }}
                                    </div>
                                    <kv-button
                                        v-if="statusError.toLowerCase().includes('bank slip') || statusError.toLowerCase().includes('boleto')"
                                        variant="outlined"
                                        prepend-icon="ui-file-02"
                                        @click="scrollToBankSlip"
                                    >
                                        {{ t('invoices-attach_bank_slip') }}
                                    </kv-button>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import InvoiceStatusChanger from './invoice-status-changer.vue';
import Icon from '../../components/icon/icon.vue';
import { extractApiError } from '../helpers';

export default {
    name: "invoice-detail-header",
    components: {
        InvoiceStatusChanger,
        Icon
    },
    props: {
        invoice: { type: Object, required: true },
        isAdmin: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        editSaving: { type: Boolean, default: false }
    },
    data() {
        return {
            logoFailed: false,
            statusError: null,
            localTitle: this.invoice.title || '',
            localDueDate: this.invoice.due_date ? this.invoice.due_date.substring(0, 10) : '',
            localCreatedAt: this.invoice.created_at ? this.invoice.created_at.substring(0, 10) : '',
            localPlaceName: this.invoice.place_name || this.invoice.place_legal_name || '',
            localPlaceLegalName: this.invoice.place_legal_name || '',
            localPlaceTaxId: this.invoice.place_tax_id || '',
            localPlacePhone: this.invoice.place_phone || '',
            localPlaceEmail: this.invoice.place_email || '',
            localPlaceAddress1: this.invoice.place_address_1 || '',
            localPlaceAddress2: this.invoice.place_address_2 || '',
            localPlaceZone: this.invoice.place_zone || '',
            localPlaceCity: this.invoice.place_city || '',
            localPlaceState: this.invoice.place_state || '',
            localPlaceZipCode: this.invoice.place_zip_code || '',
            localBillingCycleStart: this.invoice.billing_cycle_start ? this.invoice.billing_cycle_start.substring(0, 10) : '',
            localBillingCycleEnd: this.invoice.billing_cycle_end ? this.invoice.billing_cycle_end.substring(0, 10) : ''
        };
    },
    watch: {
        invoice: {
            deep: true,
            handler() {
                if (!this.editMode) {
                    this.resetLocalFields();
                }
            }
        },
        editMode(val) {
            if (!val) {
                this.resetLocalFields();
            }
        }
    },
    computed: {
        invoiceRef() {
            const year = this.invoice.created_at
                ? new Date(this.invoice.created_at).getFullYear()
                : new Date().getFullYear();
            const shortId = String(this.invoice.id || '').slice(-3).padStart(3, '0');
            return `INV-${year}-${shortId}`;
        },
        periodLabel() {
            if (!this.invoice.billing_cycle_start) return '';
            const start = new Date(this.invoice.billing_cycle_start + 'T00:00:00');
            const end = this.invoice.billing_cycle_end ? new Date(this.invoice.billing_cycle_end + 'T00:00:00') : null;
            if (end) {
                 return `${start.toLocaleDateString('pt-BR')} - ${end.toLocaleDateString('pt-BR')}`;
            }
            return start.toLocaleDateString('pt-BR');
        },
        formattedPeriodLabel() {
            return this.periodLabel;
        },
        fullAddress() {
            const parts = [
                this.invoice.place_address_1,
                this.invoice.place_address_2,
                this.invoice.place_zone,
                this.invoice.place_city,
                this.invoice.place_state,
                this.invoice.place_zip_code
            ].filter(p => !!p);
            return parts.length ? parts.join(', ') : this.t('invoices-not_informed');
        },
        canPay() {
            if (!this.invoice) return false;
            if (this.invoice.status === 'paid' || this.invoice.status === 'cancelled') return false;
            return !!(this.invoice.bank_slip_url || this.invoice.payment_link_url);
        },
        payIcon() {
            if (this.invoice && this.invoice.payment_link_url && !this.invoice.bank_slip_url) {
                return 'ui-credit-card-refresh';
            }
            return 'ui-bank-note-01';
        }
    },
    methods: {
        resetLocalFields() {
            this.localTitle = this.invoice.title || '';
            this.localDueDate = this.invoice.due_date ? this.invoice.due_date.substring(0, 10) : '';
            this.localCreatedAt = this.invoice.created_at ? this.invoice.created_at.substring(0, 10) : '';
            this.localPlaceName = this.invoice.place_name || this.invoice.place_legal_name || '';
            this.localPlaceLegalName = this.invoice.place_legal_name || '';
            this.localPlaceTaxId = this.invoice.place_tax_id || '';
            this.localPlacePhone = this.invoice.place_phone || '';
            this.localPlaceEmail = this.invoice.place_email || '';
            this.localPlaceAddress1 = this.invoice.place_address_1 || '';
            this.localPlaceAddress2 = this.invoice.place_address_2 || '';
            this.localPlaceZone = this.invoice.place_zone || '';
            this.localPlaceCity = this.invoice.place_city || '';
            this.localPlaceState = this.invoice.place_state || '';
            this.localPlaceZipCode = this.invoice.place_zip_code || '';
            this.localBillingCycleStart = this.invoice.billing_cycle_start ? this.invoice.billing_cycle_start.substring(0, 10) : '';
            this.localBillingCycleEnd = this.invoice.billing_cycle_end ? this.invoice.billing_cycle_end.substring(0, 10) : '';
        },
        formatDate(dateStr) {
            if (!dateStr) return '—';
            const normalized = String(dateStr).replace(' ', 'T');
            const d = new Date(normalized);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString('pt-BR');
        },
        scrollToBankSlip() {
            const el = document.getElementById('attach-bank-slip-section');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        handlePay() {
            if (!this.invoice) return;
            const hasSlip = !!this.invoice.bank_slip_url;
            const hasLink = !!this.invoice.payment_link_url;

            if (hasSlip && hasLink) {
                this.$emit('open-payment-modal');
            } else if (hasLink) {
                window.open(this.invoice.payment_link_url, '_blank');
            } else if (hasSlip) {
                window.open(this.invoice.bank_slip_url, '_blank');
            }
        },
        openBankSlip() {
            if (this.invoice && this.invoice.bank_slip_url) {
                window.open(this.invoice.bank_slip_url, '_blank');
            }
        },
        exportToPdf() {
            const sanitize = str => str.replace(/[/\\:*?"<>|]/g, '').trim();
            const placeName    = sanitize(this.invoice.place_name || this.invoice.place_legal_name || '');
            const invoiceTitle = sanitize(this.invoice.title || `Fatura #${this.invoice.id}`);
            const filename     = [placeName, invoiceTitle].filter(Boolean).join(' - ');

            const originalTitle = document.title;
            document.title = filename;

            window.addEventListener('afterprint', () => {
                document.title = originalTitle;
            }, { once: true });

            window.print();
        },
        emitHeaderChange() {
            this.$emit('header-change', {
                title: this.localTitle,
                place_name: this.localPlaceName,
                due_date: this.localDueDate,
                created_at: this.localCreatedAt,
                place_legal_name: this.localPlaceLegalName || this.localPlaceName,
                place_tax_id: this.localPlaceTaxId,
                place_phone: this.localPlacePhone,
                place_email: this.localPlaceEmail,
                place_address_1: this.localPlaceAddress1,
                place_address_2: this.localPlaceAddress2,
                place_zone: this.localPlaceZone,
                place_city: this.localPlaceCity,
                place_state: this.localPlaceState,
                place_zip_code: this.localPlaceZipCode,
                billing_cycle_start: this.localBillingCycleStart,
                billing_cycle_end: this.localBillingCycleEnd
            });
        },
        onZipCodeInput() {
            this.emitHeaderChange();
            const digits = this.localPlaceZipCode ? this.localPlaceZipCode.replace(/\D/g, "") : "";
            if (digits.length === 8) {
                this.fetchAddress();
            }
        },
        async fetchAddress() {
            const cep = this.localPlaceZipCode ? this.localPlaceZipCode.replace(/\D/g, "") : "";
            if (cep.length !== 8) return;

            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();

                if (data.erro) return;

                if (data.logradouro) this.localPlaceAddress1 = data.logradouro;
                if (data.bairro) this.localPlaceZone = data.bairro;
                if (data.localidade) this.localPlaceCity = data.localidade;
                if (data.uf) this.localPlaceState = data.uf;

                this.emitHeaderChange();

            } catch (error) {
                console.error("Error fetching address: ", error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.invoice-header-wrapper {
    margin-bottom: 24px;
}

.action-bar {
    .btn-ghost {
        color: #374151;
        font-weight: 500;
        font-size: 14px;
        padding: 8px 12px;
        border-radius: 12px;
        margin-left: -12px;
        transition: background-color 0.2s;
        
        i {
            font-size: 14px;
            margin-right: 8px;
            display: flex;
            align-items: center;
        }

        &:hover {
            background-color: #F3F4F6;
        }
    }

    .actions-group {
        gap: 12px;
        width: fit-content;
        margin-left: auto;
        
        .btn {
            border-radius: 12px;
            font-size: 14px;
            font-weight: 500;
            padding: 8px 16px;
            display: inline-flex;
            align-items: center;
            
            i {
                margin-right: 8px;
            }
        }

        .btn-outline-secondary {
            border-color: #E5E7EB;
            color: #374151;
            background-color: #FFF;
            
            &:hover {
                background-color: #F9FAFB;
                border-color: #D1D5DB;
            }
        }

        .btn-primary {
            background-color: #2aaa4c;
            border-color: #2aaa4c;
            color: #FFF;
            
            &:hover {
                background-color: #00542e;
                border-color: #00542e;
            }
        }
    }
}

.header-card {
    border-radius: 16px;
    border: 1px solid #E5E7EB !important;
    
    .card-body {
        padding: 32px;
    }

    @media (max-width: 768px) {
        .card-body {
            padding: 20px !important;
        }
    }

    .group-gap {
        gap: 24px;
    }

    .place-icon-container {
        width: 80px;
        height: 80px;
        border-radius: 16px;
        flex-shrink: 0;
        overflow: hidden;
        background-color: #F9FAFB;
        border: 1px solid #F3F4F6;

        @media (max-width: 768px) {
            width: 64px;
            height: 64px;
            border-radius: 12px;
        }

        .place-logo {
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 4px;
        }

        .ui-icon {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .place-name {
        font-size: 20px;
        font-weight: 500;
        color: #111827;
        margin-bottom: 2px !important;

        @media (max-width: 768px) {
            font-size: 18px;
        }
    }

    .establishment-meta {
        font-size: 13px;
        color: #6B7280;
        
        .meta-grid {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .meta-item {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            
            i {
                font-size: 13px;
                color: #9CA3AF;
                width: 16px;
                text-align: center;
            }
        }

        .address-item {
            color: #6B7280;
            font-style: normal;
            margin-top: 2px;
        }
    }

    .invoice-title-right {
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        margin-top: 4px;
    }

    .period-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background-color: #F3F4F6;
        padding: 4px 12px;
        border-radius: 100px;
        font-size: 12px;
        white-space: nowrap;
        
        .text-muted {
            color: #6B7280 !important;
        }
        
        .val {
            font-weight: 500;
            color: #374151;
        }
    }



    .date-val {
        font-size: 14px;
        font-weight: 500;
        color: #111827;
    }

    .edit-field-input {
        border: 1px solid #D1FAE5;
        border-radius: 8px;
        padding: 4px 10px;
        font-size: 14px;
        color: #111827;
        background: #fff;
        outline: none;
        transition: border-color 0.15s, box-shadow 0.15s;

        &:focus {
            border-color: #2aaa4c;
            box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.15);
        }

        &.title-input {
            font-size: 14px;
            font-weight: 500;
            width: 100%;
            max-width: 260px;
            color: #374151;
        }

         &.place_name_input {
            font-size: 18px;
            font-weight: 500;
            width: 100%;
            max-width: 400px;
        }

        &.small-input {
            font-size: 12px;
            padding: 2px 8px;
            height: 24px;
        }

        &.tiny-input {
            font-size: 11px;
            padding: 1px 6px;
            height: 20px;
            background: #fdfdfd;
        }

        &.edit-field-phone { width: 100%; max-width: 120px; }
        &.edit-field-email { width: 100%; max-width: 180px; }
        &.edit-field-address { width: 100%; max-width: 180px; }
        &.edit-field-complement { width: 100%; max-width: 80px; }
        &.edit-field-zone { width: 100%; max-width: 100px; }
        &.edit-field-state { width: 100%; max-width: 40px; }
        &.edit-field-zip { width: 100%; max-width: 80px; }
        &.edit-field-date { width: 100%; max-width: 125px; }
    }

    .edit-fields-gap {
        gap: 4px;
    }

    .invoice-details-column {
        .invoice-title-right {
            font-size: 16px;
            font-weight: 500;
            color: #111827;
        }
        
        .title-input-right {
            font-size: 16px;
            font-weight: 500;
            color: #111827;
            width: 100%;
            max-width: 300px;
            padding: 4px 10px;
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            
            &:focus {
                border-color: #2aaa4c;
                outline: none;
            }
        }
    }

    .status-error-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        
        .btn {
            height: 32px;
            font-size: 12px;
            border-radius: 8px;
            padding: 0 12px;
        }
    }
}

</style>
